/**
 * 侧边栏组件 - 参考 makeverse.cn 设计
 *
 * 提供作者中心的导航功能，包括：
 * - 工作区入口
 * - 智能体入口
 * - 登出按钮
 * - Token 使用量配额显示
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { workspaceApi } from '../../services/workspaceApi';
import { useAuthStore } from '../../store/authStore';

// 图标组件
const WorkspacesIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const AgentsIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ProfileIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MemoryIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const RechargeIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const AdminIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const LogoutIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

interface SidebarProps {
  onLogout?: () => void;
  collapsed?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navItems: NavItem[] = [
  {
    id: 'workspaces',
    label: '工作区',
    icon: <WorkspacesIcon />,
    path: '/author-center',
  },
  {
    id: 'agents',
    label: '智能体',
    icon: <AgentsIcon />,
    path: '/agents',
  },
  {
    id: 'memory',
    label: '创作记忆',
    icon: <MemoryIcon />,
    path: '/memory',
  },
  {
    id: 'recharge',
    label: '充值',
    icon: <RechargeIcon />,
    path: '/recharge',
  },
  {
    id: 'profile',
    label: '个人资料',
    icon: <ProfileIcon />,
    path: '/profile',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ onLogout, collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAdmin = useAuthStore((state) => state.user?.is_admin === true);

  const [tokenQuota, setTokenQuota] = useState<{
    used_tokens: number;
    daily_limit: number;
    percentage: number;
  } | null>(null);

  const fetchTokenQuota = useCallback(async () => {
    try {
      const data = await workspaceApi.getUserTokenQuota();
      setTokenQuota(data);
    } catch {
      setTokenQuota(null);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTokenQuota();
      const interval = setInterval(fetchTokenQuota, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchTokenQuota]);

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-100">
      {/* Logo区域 */}
      <div className="flex-shrink-0 p-5 border-b border-gray-100">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">星</span>
          </div>
          {!collapsed && (
            <div>
              <span className="text-lg font-bold text-gray-900">星曜</span>
              <p className="text-xs text-gray-500">灵感落笔，文字成章</p>
            </div>
          )}
        </a>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 py-4 px-3">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-150
                    ${active
                      ? 'bg-gray-50 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <span className={`flex-shrink-0 ${active ? 'text-gray-900' : 'text-gray-400'}`}>
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <span className="text-sm">{item.label}</span>
                  )}
                </button>
              </li>
            );
          })}
          {isAdmin && (
            <li>
              <button
                onClick={() => handleNavClick('/admin/safe')}
                className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-150
                    ${isActive('/admin')
                      ? 'bg-gray-50 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                title={collapsed ? '管理后台' : undefined}
              >
                <span className={`flex-shrink-0 ${isActive('/admin') ? 'text-gray-900' : 'text-gray-400'}`}>
                  <AdminIcon />
                </span>
                {!collapsed && (
                  <span className="text-sm">管理后台</span>
                )}
              </button>
            </li>
          )}
        </ul>
      </nav>

      {/* 资源配额区域 */}
      {!collapsed && (
        <div className="px-4 py-4 border-t border-gray-100">
          <div className="text-xs text-gray-500 mb-2">今日 Token</div>
          {tokenQuota ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      tokenQuota.percentage >= 90 ? 'bg-red-500' :
                      tokenQuota.percentage >= 70 ? 'bg-yellow-500' :
                      'bg-ink-800'
                    }`}
                    style={{ width: `${Math.min(tokenQuota.percentage, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-700">{tokenQuota.percentage}%</span>
              </div>
              <div className="text-xs text-gray-400">
                已用 {(tokenQuota.used_tokens / 10000).toFixed(1)}万 / {(tokenQuota.daily_limit / 10000).toFixed(0)}万
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-400">加载中...</div>
          )}
        </div>
      )}

      {/* 底部登出按钮 */}
      <div className="flex-shrink-0 p-3 border-t border-gray-100">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-150"
          title={collapsed ? '登出' : undefined}
        >
          <LogoutIcon className="w-5 h-5 text-gray-500" />
          {!collapsed && <span className="text-sm">登出</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
